import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { UploadCloud, FileText, Image as ImageIcon, Loader2, Zap } from "lucide-react";

// OPTION A (primary): upload IKEA order / PAX plan, run AI recognition,
// then hand the extracted config + recognized items to the confirmation screen.
export default function PaxUpload({ onRecognized }) {
  const { t } = useLang();
  const pdfRef = useRef(null);
  const imgRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    setStatus(t.upload.uploading);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const fileId = up.data.id;
      setStatus(t.pax.analyzing);
      const rec = await api.post("/pax/recognize", { file_id: fileId });
      onRecognized({ fileId, fileName: file.name, recognition: rec.data.recognition });
    } catch (e) {
      toast.error(t.pax.recognizeFailed);
    } finally {
      setBusy(false);
      setStatus("");
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8" data-testid="pax-upload">
      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
        <Zap className="w-3.5 h-3.5" /> {t.pax.uploadFast}
      </div>
      <h3 className="mt-3 font-display text-xl font-bold text-stone-900">{t.pax.uploadOption}</h3>
      <p className="mt-1 text-sm text-stone-500">{t.pax.uploadDesc}</p>

      {busy ? (
        <div className="mt-6 flex flex-col items-center justify-center py-10 text-center" data-testid="pax-upload-busy">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A2F]" />
          <p className="mt-3 text-sm font-medium text-stone-700">{status}</p>
        </div>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <button type="button" onClick={() => pdfRef.current?.click()}
            className="flex flex-col items-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-6 hover:border-[#1E3A2F] hover:bg-stone-50 transition-colors"
            data-testid="pax-upload-pdf">
            <FileText className="w-7 h-7 text-[#1E3A2F]" />
            <span className="text-sm font-semibold text-stone-800">{t.pax.uploadPdf}</span>
          </button>
          <button type="button" onClick={() => imgRef.current?.click()}
            className="flex flex-col items-center gap-2 border-2 border-dashed border-stone-300 rounded-xl p-6 hover:border-[#1E3A2F] hover:bg-stone-50 transition-colors"
            data-testid="pax-upload-photo">
            <ImageIcon className="w-7 h-7 text-[#1E3A2F]" />
            <span className="text-sm font-semibold text-stone-800">{t.pax.uploadPhoto}</span>
          </button>
        </div>
      )}
      <p className="mt-3 text-xs text-stone-400 flex items-center gap-1.5"><UploadCloud className="w-3.5 h-3.5" /> {t.upload.types}</p>

      <input ref={pdfRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }} data-testid="pax-upload-pdf-input" />
      <input ref={imgRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }} data-testid="pax-upload-img-input" />
    </div>
  );
}
